'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TechFeature {
  icon: string;
  title: string;
  description: string;
  details: string[];
}

interface TechnologyStackProps {
  features: TechFeature[];
}

export default function TechnologyStack({ features }: TechnologyStackProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-extrabold text-primary mb-4">
            Built for privacy and reliability
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            ParkPing uses a simple, secure relay model that is easy for users and easy to trust.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-4">
            {features.map((feature, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-full text-left p-6 rounded-xl transition-all duration-300 border ${
                  activeIndex === index
                    ? 'bg-accent/10 border-accent/30'
                    : 'bg-card border-border hover:bg-muted hover:border-accent/20'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      activeIndex === index
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-muted text-primary'
                    }`}
                  >
                    <Icon name={feature.icon as any} size={22} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-card rounded-2xl p-8 border border-border shadow-card lg:sticky lg:top-24">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center shadow-subtle">
                  <Icon
                    name={features[activeIndex].icon as any}
                    size={28}
                    className="text-accent-foreground"
                  />
                </div>
                <h3 className="text-2xl font-heading font-bold text-primary">
                  {features[activeIndex].title}
                </h3>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                {features[activeIndex].description}
              </p>

              <div className="space-y-4 pt-2">
                {features[activeIndex].details.map((detail, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full border border-accent/20 flex items-center justify-center mt-1">
                      <Icon name="CheckIcon" size={14} className="text-accent" />
                    </div>
                    <p className="text-foreground">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
